/**
 * AddressCard — structured, editable, AI-completable address block for the
 * lead detail drawer. View → Edit form (with Google autocomplete) → AI
 * suggestions with confidence/source review before saving.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin, Loader2, Sparkles, Pencil, Copy, ExternalLink, Check, X, Search,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  verified:            { pt: "Endereço verificado", en: "Verified Address", cls: "bg-emerald-500/15 text-emerald-500" },
  manually_confirmed:  { pt: "Confirmado manualmente", en: "Manually Confirmed", cls: "bg-emerald-500/15 text-emerald-500" },
  ai_suggested:        { pt: "Sugerido pela IA", en: "AI Suggested", cls: "bg-blue-500/15 text-blue-500" },
  needs_review:        { pt: "Precisa de revisão", en: "Needs Review", cls: "bg-amber-500/15 text-amber-500" },
  multiple_matches:    { pt: "Múltiplas opções", en: "Multiple Matches", cls: "bg-amber-500/15 text-amber-500" },
  invalid:             { pt: "Inválido", en: "Invalid", cls: "bg-red-500/15 text-red-500" },
  unverified:          { pt: "Não verificado", en: "Unverified", cls: "bg-gray-500/15 text-gray-400" },
};

const EMPTY = {
  street_address: "", address_line_2: "", city: "", state: "", zip_code: "",
  county: "", country: "United States", latitude: "", longitude: "",
};

export default function AddressCard({ lead, onChanged, isPt = true }) {
  const [mode, setMode] = useState("view"); // view | edit | ai
  const [fields, setFields] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [preds, setPreds] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    setMode("view"); setAiResult(null); setPreds([]);
    setFields({
      street_address: lead.street_address || "",
      address_line_2: lead.address_line_2 || "",
      city: lead.city || "", state: lead.state || "",
      zip_code: lead.zip_code || "", county: lead.county || "",
      country: lead.country || "United States",
      latitude: lead.latitude ?? "", longitude: lead.longitude ?? "",
    });
  }, [lead.id]); // eslint-disable-line

  const formatted = lead.formatted_address || lead.address || "";
  const status = lead.address_status || "unverified";
  const badge = STATUS_BADGE[status] || STATUS_BADGE.unverified;

  const gmapsUrl = formatted
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formatted)}`
    : null;

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(formatted);
      toast.success(isPt ? "Endereço copiado" : "Address copied");
    } catch (_e) { toast.error("Copy failed"); }
  };

  const runAI = async () => {
    setAiLoading(true); setAiResult(null); setMode("ai");
    try {
      const { data } = await axios.post(`${API}/leads/${lead.id}/address/complete`, {});
      setAiResult(data);
      if (!data.ok) {
        toast.warning(isPt
          ? "Não conseguimos determinar o endereço com confiança suficiente."
          : "We could not determine the address with sufficient confidence.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(detail || (isPt ? "Falha na análise de endereço" : "Address analysis failed"));
      setMode("view");
    } finally { setAiLoading(false); }
  };

  const saveAddress = async (payload) => {
    setSaving(true);
    try {
      await axios.post(`${API}/leads/${lead.id}/address/save`, payload);
      toast.success(isPt ? "Endereço salvo" : "Address saved");
      setMode("view"); setAiResult(null);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isPt ? "Falha ao salvar" : "Save failed"));
    } finally { setSaving(false); }
  };

  const confirmSuggestion = (s) => saveAddress({
    street_address: s.street_address || "", address_line_2: s.address_line_2 || "",
    city: s.city || "", state: s.state || "", zip_code: s.zip_code || "",
    county: s.county || "", country: s.country || "United States",
    formatted_address: s.formatted_address || "",
    latitude: s.latitude ?? null, longitude: s.longitude ?? null,
    timezone: s.timezone || "",
    address_status: "verified", address_confidence: s.confidence,
    address_source: s.source,
  });

  const editFromSuggestion = (s) => {
    setFields({
      street_address: s.street_address || "", address_line_2: s.address_line_2 || "",
      city: s.city || "", state: s.state || "", zip_code: s.zip_code || "",
      county: s.county || "", country: s.country || "United States",
      latitude: s.latitude ?? "", longitude: s.longitude ?? "",
    });
    setMode("edit"); setAiResult(null);
  };

  const saveManual = () => saveAddress({
    ...fields,
    latitude: fields.latitude === "" ? null : Number(fields.latitude),
    longitude: fields.longitude === "" ? null : Number(fields.longitude),
    formatted_address: "",
    address_status: "manually_confirmed", address_confidence: null,
    address_source: "manual",
  });

  const onStreetChange = (v) => {
    setFields((f) => ({ ...f, street_address: v }));
    clearTimeout(debounceRef.current);
    if (v.trim().length < 4) { setPreds([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/address/autocomplete`, { params: { q: v } });
        setPreds(data.predictions || []);
      } catch (_e) { setPreds([]); }
    }, 350);
  };

  const pickPrediction = async (p) => {
    setPreds([]);
    try {
      const { data } = p.resolved
        ? { data: { ok: true, address: p.resolved } }
        : await axios.post(`${API}/address/resolve`, { place_id: p.place_id, description: p.description });
      if (data.ok && data.address) {
        const a = data.address;
        setFields({
          street_address: a.street_address || "", address_line_2: a.address_line_2 || "",
          city: a.city || "", state: a.state || "", zip_code: a.zip_code || "",
          county: a.county || "", country: a.country || "United States",
          latitude: a.latitude ?? "", longitude: a.longitude ?? "",
        });
      }
    } catch (_e) { /* keep typed value */ }
  };

  const renderField = (k, label, half, rest = {}) => (
    <div key={k} className={half ? "w-[calc(50%-4px)]" : "w-full"}>
      <label className="block text-[10px] uppercase tracking-wide text-[var(--al-text-muted)] mb-0.5">{label}</label>
      <input
        data-testid={`addr-input-${k}`}
        value={fields[k]}
        onChange={(e) => setFields((f) => ({ ...f, [k]: e.target.value }))}
        className="al-input h-8 text-[12px] w-full"
        {...rest}
      />
    </div>
  );

  return (
    <div data-testid="ldd-address-card" className="rounded-lg border border-[var(--al-border)] bg-[var(--al-row-alt)] p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold">
          <MapPin className="w-3 h-3" /> {isPt ? "Endereço" : "Address"}
        </span>
        <span data-testid="addr-status-badge" className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded ${badge.cls}`}>
          {isPt ? badge.pt : badge.en}
          {lead.address_confidence ? ` · ${lead.address_confidence}%` : ""}
        </span>
      </div>

      {mode === "view" && (
        <>
          <div data-testid="addr-formatted" className="text-[12.5px] text-[var(--al-text)] whitespace-pre-line">
            {lead.street_address ? (
              <>
                {lead.street_address}{lead.address_line_2 ? `, ${lead.address_line_2}` : ""}{"\n"}
                {[lead.city, [lead.state, lead.zip_code].filter(Boolean).join(" ")].filter(Boolean).join(", ")}{"\n"}
                {[lead.county, lead.country].filter(Boolean).join(", ")}
              </>
            ) : (formatted || <span className="italic text-[var(--al-text-muted)]">{isPt ? "Sem endereço" : "No address yet"}</span>)}
          </div>
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            <button type="button" data-testid="addr-ai-btn" onClick={runAI} disabled={aiLoading}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#1B3A8A] hover:bg-[#234aac] text-white text-[10.5px] font-bold disabled:opacity-60">
              {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {isPt ? "Completar com IA" : "Complete with AI"}
            </button>
            <button type="button" data-testid="addr-edit-btn" onClick={() => setMode("edit")}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[var(--al-border)] text-[10.5px] font-semibold text-[var(--al-text-muted)] hover:text-[var(--al-text)]">
              <Pencil className="w-3 h-3" /> {isPt ? "Editar" : "Edit"}
            </button>
            {gmapsUrl && (
              <a href={gmapsUrl} target="_blank" rel="noopener noreferrer" data-testid="addr-gmaps-btn"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[var(--al-border)] text-[10.5px] font-semibold text-[var(--al-text-muted)] hover:text-[#EA4335]">
                <ExternalLink className="w-3 h-3" /> Google Maps
              </a>
            )}
            {formatted && (
              <button type="button" data-testid="addr-copy-btn" onClick={copyAddress}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[var(--al-border)] text-[10.5px] font-semibold text-[var(--al-text-muted)] hover:text-[var(--al-text)]">
                <Copy className="w-3 h-3" /> {isPt ? "Copiar" : "Copy"}
              </button>
            )}
          </div>
        </>
      )}

      {mode === "edit" && (
        <div className="space-y-2">
          <div className="relative">
            <label className="block text-[10px] uppercase tracking-wide text-[var(--al-text-muted)] mb-0.5">
              {isPt ? "Endereço (rua)" : "Street Address"}
            </label>
            <input
              data-testid="addr-input-street_address"
              value={fields.street_address}
              onChange={(e) => onStreetChange(e.target.value)}
              placeholder="3033 Nicholson Dr"
              className="al-input h-8 text-[12px] w-full"
            />
            {preds.length > 0 && (
              <div data-testid="addr-autocomplete-list" className="absolute z-20 mt-1 w-full rounded-md border border-[var(--al-border)] bg-[var(--al-card-bg)] shadow-lg max-h-44 overflow-y-auto">
                {preds.map((p, i) => (
                  <button key={i} type="button" data-testid={`addr-autocomplete-${i}`}
                    onClick={() => pickPrediction(p)}
                    className="block w-full text-left px-2.5 py-1.5 text-[11.5px] hover:bg-[var(--al-row)] text-[var(--al-text)]">
                    {p.description}
                  </button>
                ))}
              </div>
            )}
          </div>
          {renderField("address_line_2", "Address Line 2", false, { placeholder: "Ste 100" })}
          <div className="flex gap-2 flex-wrap">
            {renderField("city", isPt ? "Cidade" : "City", true)}
            {renderField("state", isPt ? "Estado" : "State", true, { placeholder: "FL", maxLength: 20 })}
            {renderField("zip_code", "ZIP Code", true, { placeholder: "32814" })}
            {renderField("county", "County", true)}
            {renderField("country", isPt ? "País" : "Country", true)}
            {renderField("latitude", "Latitude", true)}
            {renderField("longitude", "Longitude", true)}
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" data-testid="addr-cancel-btn" onClick={() => { setMode("view"); setPreds([]); }}
              className="h-8 px-3 rounded-lg border border-[var(--al-border)] text-[11.5px] text-[var(--al-text-muted)]">
              {isPt ? "Cancelar" : "Cancel"}
            </button>
            <button type="button" data-testid="addr-save-btn" onClick={saveManual} disabled={saving}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[11.5px] font-semibold disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {isPt ? "Salvar endereço" : "Save Address"}
            </button>
          </div>
        </div>
      )}

      {mode === "ai" && (
        <div className="space-y-2" data-testid="addr-ai-panel">
          {aiLoading ? (
            <div className="flex items-center gap-2 py-3 text-[11.5px] text-[var(--al-text-muted)]">
              <Loader2 className="w-4 h-4 animate-spin text-[#3B82F6]" />
              {isPt ? "Analisando endereço..." : "Analyzing address..."}
            </div>
          ) : aiResult && aiResult.suggestions?.length > 0 ? (
            <>
              <div className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold">
                {aiResult.suggestions.length > 1
                  ? (isPt ? "Encontramos múltiplos endereços possíveis:" : "We found multiple possible addresses.")
                  : (isPt ? "Endereço sugerido" : "Suggested Address")}
              </div>
              {aiResult.suggestions.map((s, i) => (
                <div key={i} data-testid={`addr-suggestion-${i}`}
                  className="rounded-md border border-[var(--al-border)] bg-[var(--al-bg)] p-2.5 space-y-1.5">
                  <div className="text-[12px] text-[var(--al-text)] whitespace-pre-line">
                    {s.street_address}{s.address_line_2 ? `, ${s.address_line_2}` : ""}{"\n"}
                    {[s.city, [s.state, s.zip_code].filter(Boolean).join(" ")].filter(Boolean).join(", ")}{"\n"}
                    {[s.county, s.country].filter(Boolean).join(", ")}
                  </div>
                  <div className="text-[10px] text-[var(--al-text-muted)]">
                    Confidence: <strong>{s.confidence}%</strong> · Source: {s.source}
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    <button type="button" data-testid={`addr-confirm-${i}`} onClick={() => confirmSuggestion(s)} disabled={saving}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[10.5px] font-bold disabled:opacity-60">
                      {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      {isPt ? "Confirmar e salvar" : "Confirm and Save"}
                    </button>
                    <button type="button" data-testid={`addr-edit-sugg-${i}`} onClick={() => editFromSuggestion(s)}
                      className="px-2 py-1 rounded-md border border-[var(--al-border)] text-[10.5px] font-semibold text-[var(--al-text-muted)]">
                      {isPt ? "Editar endereço" : "Edit Address"}
                    </button>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="text-[11.5px] text-[var(--al-text-muted)] py-2">
              {isPt
                ? "Não encontramos um endereço com confiança suficiente. Tente editar manualmente."
                : "We could not determine the address with sufficient confidence. Try editing manually."}
            </div>
          )}
          {!aiLoading && (
            <div className="flex gap-1.5">
              <button type="button" data-testid="addr-search-again" onClick={runAI}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[var(--al-border)] text-[10.5px] font-semibold text-[var(--al-text-muted)]">
                <Search className="w-3 h-3" /> {isPt ? "Buscar de novo" : "Search Again"}
              </button>
              <button type="button" data-testid="addr-ai-cancel" onClick={() => { setMode("view"); setAiResult(null); }}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[var(--al-border)] text-[10.5px] font-semibold text-[var(--al-text-muted)]">
                <X className="w-3 h-3" /> {isPt ? "Cancelar" : "Cancel"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
